/**
 * ActCard (S130, Story Bible §6.4) — the diegetic transition card at the two
 * act doors. On `wave.started` 13 ("ACT II — HER SIGNAL") and 25 ("ACT III —
 * DOMINION") a board-centered card CRT-cuts in, holds a beat, and fades itself
 * out. There is deliberately NO card at wave 35 — the finale belongs to the
 * boss, the sigil, and the endings.
 *
 * Flow contract (the GDD law, §7 of the Bible): `pointer-events: none`,
 * input-transparent, auto-dismissing — it can never block the tower defense
 * even in principle; the wave plays under it. Render-only: listens to an
 * already-emitted event, draws DOM, emits nothing, reads no sim state.
 *
 * The act doors are exactly the frames the S126 corruption steps up, so the
 * card *names* what the board starts showing. Color comes from the central
 * ECHELON tokens (her magenta, D-224) — no new hex. Reduced motion: a plain
 * fade, no glitch-cut. The title is `announce()`d (S125) for screen readers.
 */
import type { EventBus } from "../core/EventBus.ts";
import { GAME } from "../core/config.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { t } from "../utils/i18n.ts";
import { announce } from "./a11y.ts";
import { UI_FONT_STACK, roleFontSize, withUiScale } from "./uiTokens.ts";

/** How long the card owns the act door before fading (display + fade). */
const HOLD_MS = 3600;
const FADE_MS = 900;

export class ActCard {
  private readonly parent: HTMLElement;
  private readonly onArrival: ((act: 2 | 3) => void) | undefined;
  private el: HTMLElement | null = null;
  private shown = new Set<number>();
  private timers: number[] = [];
  private unsubs: Array<() => void> = [];

  /**
   * @param onArrival WOW Pass 2 (item 3) — fired once, the moment an act door
   *   card shows, so the composition layer can layer the screen-space
   *   ESCALATION beat (sweep/sting) the act crossing lacked (the boss edge
   *   already had one). Optional → ActCard stays a pure DOM card without it.
   */
  constructor(parent: HTMLElement, onArrival?: (act: 2 | 3) => void) {
    this.parent = parent;
    this.onArrival = onArrival;
    injectStyles();
  }

  attach(bus: EventBus): void {
    this.detach();
    this.unsubs.push(
      bus.on("wave.started", ({ wave }) => {
        if (wave === 1) this.resetRunState();
        if (wave === GAME.WAVES_ACT_I + 1) this.show(2);
        if (wave === GAME.WAVES_ACT_II + 1) this.show(3);
      }),
    );
  }

  /** Re-arm for a new run (also called on wave 1). */
  resetRunState(): void {
    this.shown.clear();
    this.dismiss();
  }

  detach(): void {
    for (const off of this.unsubs) off();
    this.unsubs = [];
    this.resetRunState();
  }

  private show(act: 2 | 3): void {
    if (this.shown.has(act)) return;
    this.shown.add(act);
    // WOW Pass 2 (item 3) — the act-arrival escalation beat fires with the card
    // (once per door per run; re-armed on wave 1 with the rest of run state).
    this.onArrival?.(act);
    this.dismiss();

    const title = t(`act.card.${act}.title`);
    const sub = t(`act.card.${act}.sub`);

    const el = document.createElement("div");
    el.className = `act-card act-card-${act}`;
    if (isReducedMotion()) el.classList.add("act-card-reduced");
    el.setAttribute("aria-hidden", "true"); // announce() carries the a11y copy
    el.innerHTML = `
      <div class="act-card-rule"></div>
      <div class="act-card-title"></div>
      <div class="act-card-sub"></div>
      <div class="act-card-rule"></div>`;
    (el.querySelector(".act-card-title") as HTMLElement).textContent = title;
    (el.querySelector(".act-card-sub") as HTMLElement).textContent = sub;

    this.parent.appendChild(el);
    this.el = el;
    announce(`${title} — ${sub}`);

    this.timers.push(
      window.setTimeout(() => el.classList.add("act-card-out"), HOLD_MS),
      window.setTimeout(() => this.dismiss(), HOLD_MS + FADE_MS),
    );
  }

  private dismiss(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers = [];
    this.el?.remove();
    this.el = null;
  }
}

function injectStyles(): void {
  if (typeof document === "undefined" || document.getElementById("act-card-styles")) {
    return;
  }
  const style = document.createElement("style");
  style.id = "act-card-styles";
  style.textContent = `
    .act-card {
      position: absolute; inset: 0; z-index: 40;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 10px; pointer-events: none;
      font-family: ${UI_FONT_STACK};
      text-align: center;
      animation: act-card-in 0.5s ease-out;
    }
    .act-card .act-card-rule {
      width: min(420px, 56%); height: 1px;
      background: linear-gradient(90deg, transparent, var(--act-ac), transparent);
      opacity: 0.7;
    }
    .act-card .act-card-title {
      /* BL-191 scale-aware via the §9 display role (34px @ scale 1). */
      font-size: ${roleFontSize("display")}; letter-spacing: 0.32em; line-height: 1.2;
      color: var(--act-ac);
      text-shadow: 0 0 18px color-mix(in srgb, var(--act-ac) 70%, transparent),
                   0 2px 6px rgba(0,0,0,0.9);
    }
    .act-card .act-card-sub {
      /* BL-191 scale-aware (13px @ scale 1). */
      font-size: ${withUiScale(13)}; letter-spacing: 0.22em; color: #b9c9bd;
      text-shadow: 0 1px 4px rgba(0,0,0,0.9);
    }
    /* Act II — her signal arrives: the trespass magenta. Act III — dominion:
       her depth violet carries the heavier door. Central tokens only. */
    .act-card-2 { --act-ac: ${ECHELON.SIGNAL}; }
    .act-card-3 { --act-ac: ${ECHELON.DEEP}; }
    .act-card-out { animation: act-card-fade ${FADE_MS}ms ease-in forwards; }
    @keyframes act-card-in {
      0% { opacity: 0; transform: translateY(6px); }
      40% { opacity: 1; }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes act-card-fade { to { opacity: 0; } }
    .act-card.act-card-reduced { animation: none; }
    .act-card.act-card-reduced.act-card-out { animation: none; opacity: 0; transition: opacity ${FADE_MS}ms ease-in; }
    @media (prefers-reduced-motion: reduce) {
      .act-card { animation: none; }
      .act-card.act-card-out { animation: none; opacity: 0; transition: opacity ${FADE_MS}ms ease-in; }
    }
  `;
  document.head.appendChild(style);
}
